import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Block,
  SwapHoriz,
  Info,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function BannedWordsManager({ stationId }) {
  const [bannedWords, setBannedWords] = useState([]);
  const [styleGuidelines, setStyleGuidelines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWord, setEditingWord] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Form state
  const [wordForm, setWordForm] = useState({
    word_or_phrase: '',
    type: 'word',
    reason: '',
    severity: 'block',
    replacement: '',
  });

  // Bulk import state
  const [bulkImportText, setBulkImportText] = useState('');

  useEffect(() => {
    fetchBannedWords();
    fetchStyleGuidelines();
  }, [stationId]);

  const fetchBannedWords = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/banned-words`, {
        params: { station_id: stationId },
        withCredentials: true,
      });
      setBannedWords(res.data || []);
    } catch (err) {
      console.error('Error fetching banned words:', err);
    }
  };

  const fetchStyleGuidelines = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/style-guidelines`, {
        params: { station_id: stationId },
        withCredentials: true,
      });
      setStyleGuidelines(res.data || []);
    } catch (err) {
      console.error('Error fetching style guidelines:', err);
    }
  };

  const handleCreateWord = async () => {
    if (!wordForm.word_or_phrase.trim()) {
      setError('Word or phrase is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/ai/banned-words`,
        { ...wordForm, station_id: stationId },
        { withCredentials: true }
      );
      setSuccess(editingWord ? 'Word updated successfully' : 'Word added successfully');
      setDialogOpen(false);
      setEditingWord(null);
      setWordForm({
        word_or_phrase: '',
        type: 'word',
        reason: '',
        severity: 'block',
        replacement: '',
      });
      fetchBannedWords();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save word');
    } finally {
      setLoading(false);
    }
  };

  const handleEditWord = (word) => {
    setEditingWord(word);
    setWordForm({
      word_or_phrase: word.word_or_phrase,
      type: word.type || 'word',
      reason: word.reason || '',
      severity: word.severity || 'block',
      replacement: word.replacement || '',
    });
    setDialogOpen(true);
  };

  const handleDeleteWord = async (wordId) => {
    if (!window.confirm('Are you sure you want to delete this banned word?')) return;

    try {
      await axios.delete(`${API_URL}/api/ai/banned-words/${wordId}`, {
        withCredentials: true,
      });
      setSuccess('Word deleted successfully');
      fetchBannedWords();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete word');
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) {
      setError('Please enter words/phrases to import');
      return;
    }

    const words = bulkImportText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => ({
        word_or_phrase: line,
        type: line.includes(' ') ? 'phrase' : 'word',
        severity: 'block',
      }));

    if (words.length === 0) {
      setError('No valid words found');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/api/ai/banned-words/bulk`,
        { words, station_id: stationId },
        { withCredentials: true }
      );
      setSuccess(`Imported ${res.data.imported} words successfully`);
      setBulkImportText('');
      fetchBannedWords();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to import words');
    } finally {
      setLoading(false);
    }
  };

  const wordsByType = {
    word: bannedWords.filter((w) => w.type === 'word'),
    phrase: bannedWords.filter((w) => w.type === 'phrase'),
  };

  const wordsBySeverity = {
    block: bannedWords.filter((w) => w.severity === 'block'),
    replace: bannedWords.filter((w) => w.severity === 'replace'),
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Banned Words" icon={<Block />} iconPosition="start" />
        <Tab label="Bulk Import" icon={<Add />} iconPosition="start" />
        <Tab label="Style Guidelines" icon={<Info />} iconPosition="start" />
      </Tabs>

      {/* Banned Words Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                    Banned Words & Phrases ({bannedWords.length})
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      setEditingWord(null);
                      setWordForm({
                        word_or_phrase: '',
                        type: 'word',
                        reason: '',
                        severity: 'block',
                        replacement: '',
                      });
                      setDialogOpen(true);
                    }}
                    sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#0891b2' } }}
                  >
                    Add Word/Phrase
                  </Button>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                  The writer is told to avoid every word and phrase on this list before it writes a script. Words are
                  matched whole (so "like" does not match "unlike"), and nothing is ever cut out of a finished
                  script, which would leave broken sentences on air. "Block": if a blocked term still slips through,
                  the script is rewritten once and, if it remains, flagged with a warning in Generated Links.
                  "Replace": the term is swapped for your replacement text before it is voiced. Keep this list to
                  terms that really should never air; everyday words such as "like" or "really" are better handled
                  through Style Guidelines.
                </Alert>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)' }}>
                      <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                        Blocked ({wordsBySeverity.block.length})
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        The writer is told to avoid these. If one slips through, the script is rewritten once, then flagged.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)' }}>
                      <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                        Replaced ({wordsBySeverity.replace.length})
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        Swapped for their replacement text before the script is voiced.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'var(--rs-bg)', border: '1px solid var(--rs-border)' }}>
                      <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                        Total: {bannedWords.length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        {wordsByType.word.length} words, {wordsByType.phrase.length} phrases
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {bannedWords.length === 0 ? (
                  <Alert severity="info">No banned words configured. Add words/phrases to improve AI script quality.</Alert>
                ) : (
                  <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-bg)', maxHeight: 600 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: '#22d3ee' }}>Word/Phrase</TableCell>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: '#22d3ee' }}>Type</TableCell>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: '#22d3ee' }}>Severity</TableCell>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: '#22d3ee' }}>Replacement</TableCell>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: '#22d3ee' }}>Reason</TableCell>
                          <TableCell sx={{ bgcolor: 'var(--rs-paper)', color: '#22d3ee' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bannedWords.map((word) => (
                          <TableRow key={word.id} sx={{ '&:hover': { bgcolor: 'var(--rs-paper)' } }}>
                            <TableCell sx={{ color: 'var(--rs-text)' }}>{word.word_or_phrase}</TableCell>
                            <TableCell>
                              <Chip
                                label={word.type}
                                size="small"
                                sx={{
                                  bgcolor: word.type === 'phrase' ? '#9c27b0' : '#1976d2',
                                  color: 'var(--rs-text)',
                                  fontSize: '0.7rem',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={word.severity === 'block' ? <Block /> : <SwapHoriz />}
                                label={word.severity}
                                size="small"
                                sx={{
                                  bgcolor: word.severity === 'block' ? '#f44336' : '#ff9800',
                                  color: 'var(--rs-text)',
                                  fontSize: '0.7rem',
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: '#999' }}>
                              {word.replacement || '-'}
                            </TableCell>
                            <TableCell sx={{ color: '#999', maxWidth: 200 }}>
                              {word.reason || '-'}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleEditWord(word)}
                                sx={{ color: '#22d3ee', mr: 1 }}
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteWord(word.id)}
                                sx={{ color: '#ff6b6b' }}
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Bulk Import Tab */}
      {tabValue === 1 && (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2 }}>
              Bulk Import Banned Words
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              Enter words/phrases one per line. Phrases (with spaces) will be detected automatically.
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={10}
              value={bulkImportText}
              onChange={(e) => setBulkImportText(e.target.value)}
              placeholder="um&#10;uh&#10;like&#10;you know&#10;basically&#10;literally"
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              onClick={handleBulkImport}
              disabled={loading || !bulkImportText.trim()}
              sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#0891b2' } }}
            >
              {loading ? 'Importing...' : 'Import Words'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Style Guidelines Tab */}
      {tabValue === 2 && (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2 }}>
              Style Guidelines
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              These guidelines are automatically included in AI generation prompts to improve script quality.
            </Typography>

            {styleGuidelines.length === 0 ? (
              <Alert severity="info">No style guidelines configured.</Alert>
            ) : (
              <List>
                {styleGuidelines.map((guideline) => (
                  <ListItem
                    key={guideline.id}
                    sx={{
                      bgcolor: 'var(--rs-bg)',
                      borderRadius: 1,
                      mb: 1,
                      border: '1px solid var(--rs-border)',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ color: '#22d3ee', textTransform: 'capitalize' }}>
                          {guideline.guideline_type}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" sx={{ color: 'var(--rs-text)', mb: 1 }}>
                            {guideline.content}
                          </Typography>
                          {guideline.examples && guideline.examples.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                              {guideline.examples.map((example, idx) => (
                                <Chip
                                  key={idx}
                                  label={example}
                                  size="small"
                                  sx={{ fontSize: '0.7rem', bgcolor: 'var(--rs-border)', color: 'var(--rs-text)' }}
                                />
                              ))}
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {/* Word Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingWord(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}
      >
        <DialogTitle sx={{ color: '#22d3ee' }}>
          {editingWord ? 'Edit Banned Word/Phrase' : 'Add Banned Word/Phrase'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Word or Phrase"
                value={wordForm.word_or_phrase}
                onChange={(e) => setWordForm({ ...wordForm, word_or_phrase: e.target.value })}
                required
                helperText="Enter the word or phrase to ban (case-insensitive)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Type</InputLabel>
                <Select
                  value={wordForm.type}
                  onChange={(e) => setWordForm({ ...wordForm, type: e.target.value })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="word">Word</MenuItem>
                  <MenuItem value="phrase">Phrase</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Severity</InputLabel>
                <Select
                  value={wordForm.severity}
                  onChange={(e) => setWordForm({ ...wordForm, severity: e.target.value })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="block">Block (avoid; rewrite once and flag if it slips through)</MenuItem>
                  <MenuItem value="replace">Replace (swap for the replacement text)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {wordForm.severity === 'replace' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Replacement"
                  value={wordForm.replacement}
                  onChange={(e) => setWordForm({ ...wordForm, replacement: e.target.value })}
                  helperText="Word/phrase to use instead"
                  required
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Reason (Optional)"
                value={wordForm.reason}
                onChange={(e) => setWordForm({ ...wordForm, reason: e.target.value })}
                placeholder="e.g., Filler word - not professional"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid var(--rs-border)' }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#999' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateWord}
            disabled={loading || !wordForm.word_or_phrase || (wordForm.severity === 'replace' && !wordForm.replacement)}
            sx={{ bgcolor: '#22d3ee', color: '#0a0c11', '&:hover': { bgcolor: '#0891b2' } }}
          >
            {loading ? 'Saving...' : editingWord ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
